import { useState, useEffect, useRef, useCallback } from 'react';
import { View, Pressable, ScrollView, ActivityIndicator, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { InputField } from './InputField';
import { Card } from './Card';
import { ThemedText, Body, Caption } from './Text';
import { useTheme } from '@/theme';
// import { geocodeAddress } from '@/services/maps'; // Not used (Cloud Function is preferred)
import { functions, httpsCallableWithTimeout } from '@/lib/functions';
import { DESTINATION_OPTIONS } from '@/data/destinationOptions';
import { toIso2CountryCode } from '@/utils/countryIso2';

export type CitySuggestion = {
  city: string;
  state?: string;
  country: string;
  countryCode?: string; // ISO2 when resolvable
  placeId?: string;
  fullName: string; // "City, State, Country" or "City, Country"
  coordinates?: { latitude: number; longitude: number };
};

const MAX_RESULTS = 8;
const DEFAULT_MIN_QUERY_LENGTH = 2;

type CityAutocompleteProps = {
  value: string;
  onChangeText: (text: string) => void;
  onSelectCity?: (city: CitySuggestion) => void;
  placeholder?: string;
  label?: string;
  errorText?: string;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  minQueryLength?: number;
  debounceMs?: number;
  /** When true, show "Use as entered" when no suggestions so user can accept typed value without coordinates */
  allowUseAsEntered?: boolean;
};

/**
 * City autocomplete component with suggestions
 * Formats cities as "City, State" or "City, Country" to handle duplicates
 */
const NO_RESULTS_HINT = "No matches. Try 'City, Country' (e.g. Paris, France) or pick a popular destination above.";

export function CityAutocomplete({
  value,
  onChangeText,
  onSelectCity,
  placeholder = 'Enter city name',
  label,
  errorText,
  autoCapitalize = 'words',
  minQueryLength = DEFAULT_MIN_QUERY_LENGTH,
  debounceMs = 250,
  allowUseAsEntered = false,
}: CityAutocompleteProps) {
  const theme = useTheme();
  const [suggestions, setSuggestions] = useState<CitySuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showNoResultsHint, setShowNoResultsHint] = useState(false);
  const [loading, setLoading] = useState(false);
  // Inline suggestions: no need for measured position or keyboard height.
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const cacheRef = useRef<Record<string, CitySuggestion[]>>({});
  const loadingKeyRef = useRef<string | null>(null);
  const lastResultsRef = useRef<CitySuggestion[]>([]);
  const inputRef = useRef<TextInput>(null);
  const isShowingRef = useRef(false);
  const isSelectingRef = useRef(false);
  const isFocusedRef = useRef(false);
  const dismissedRef = useRef(false);
  const lastSelectedValueRef = useRef<string | null>(null);

  useEffect(() => {
    // Don't search if currently selecting
    if (isSelectingRef.current) {
      return;
    }

    // If the current value is one we just selected, keep suggestions closed until the user types again.
    if (lastSelectedValueRef.current && value === lastSelectedValueRef.current) {
      setShowSuggestions(false);
      isShowingRef.current = false;
      return;
    }

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't search if value is too short or empty
    if (!value || value.trim().length < minQueryLength) {
      setSuggestions([]);
      setShowSuggestions(false);
      isShowingRef.current = false;
      return;
    }

    // Debounce search
    debounceTimerRef.current = setTimeout(async () => {
      // Double-check we're not selecting before searching
      if (!isSelectingRef.current) {
        await searchCities(value.trim());
      }
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value]);

  const buildLocalSuggestions = (query: string): CitySuggestion[] => {
    const key = query.trim().toLowerCase();
    if (!key) return [];

    // Filter the shared popular destinations; prefer prefix matches over substring.
    const opts = DESTINATION_OPTIONS
      .filter((opt) => opt.value !== 'other')
      .filter((opt) => opt.label.toLowerCase().includes(key));
    const prefixFirst = opts.sort((a, b) => {
      const aStarts = a.label.toLowerCase().startsWith(key) ? 1 : 0;
      const bStarts = b.label.toLowerCase().startsWith(key) ? 1 : 0;
      return bStarts - aStarts;
    });
    return prefixFirst.slice(0, MAX_RESULTS).map((opt) => {
      const firstPart = opt.label.split(',')[0]?.trim() || opt.label;
      const country = opt.label.includes(',') ? opt.label.split(',').slice(1).join(',').trim() : '';
      return {
        city: firstPart,
        country: country || '',
        fullName: opt.label,
        countryCode: toIso2CountryCode(country || '') || undefined,
      } satisfies CitySuggestion;
    });
  };

  const buildPopularSuggestions = (): CitySuggestion[] => {
    return DESTINATION_OPTIONS
      .filter((opt) => opt.value !== 'other')
      .slice(0, MAX_RESULTS)
      .map((opt) => {
        const firstPart = opt.label.split(',')[0]?.trim() || opt.label;
        const country = opt.label.includes(',') ? opt.label.split(',').slice(1).join(',').trim() : '';
        return {
          city: firstPart,
          country: country || '',
          fullName: opt.label,
          countryCode: toIso2CountryCode(country || '') || undefined,
        } satisfies CitySuggestion;
      });
  };

  const setSuggestionState = (next: CitySuggestion[], noResultsFromSearch = false) => {
    const limited = next.slice(0, MAX_RESULTS);
    setSuggestions(limited);
    setShowNoResultsHint(noResultsFromSearch && limited.length === 0);

    const shouldShow =
      (limited.length > 0 || (noResultsFromSearch && limited.length === 0)) &&
      !isSelectingRef.current &&
      isFocusedRef.current &&
      !dismissedRef.current;

    isShowingRef.current = shouldShow;
    setShowSuggestions(shouldShow);
  };

  const searchCities = async (query: string) => {
    // Don't search if user is selecting
    if (isSelectingRef.current) {
      return;
    }

    const key = query.toLowerCase();

    // Serve from cache when available to speed up suggestions
    if (cacheRef.current[key]) {
      const cached = cacheRef.current[key];
      setSuggestionState(cached, cached.length === 0);
      return;
    }

    // Avoid duplicate requests for the same query while a request is in flight
    if (loadingKeyRef.current === key) {
      return;
    }
    
    setLoading(true);
    loadingKeyRef.current = key;
    try {
      // Start with local suggestions so the dropdown feels responsive & stable.
      const localResults: CitySuggestion[] = buildLocalSuggestions(query);
      if (localResults.length > 0) {
        lastResultsRef.current = localResults;
        setSuggestionState(localResults, false);
      }

      const queryParts = query
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      const countryFromQuery =
        queryParts.length > 1
          ? toIso2CountryCode(queryParts.slice(1).join(' ')) || undefined
          : undefined;
      const countryFromLocal =
        localResults.find((item) => item.countryCode)?.countryCode ||
        localResults
          .filter((item) => item.city.toLowerCase().startsWith(query.toLowerCase()))
          .find((item) => item.countryCode)?.countryCode;
      const countryHint = countryFromQuery || countryFromLocal;
      let geocodeSuggestions: CitySuggestion[] = [];
      
      // Use Cloud Function directly to avoid Mapbox 403 errors
      try {
        const geocodeLocation = httpsCallableWithTimeout<
          { query: string; country?: string },
          {
            lat: number;
            lon: number;
            name: string;
            country: string;
            state?: string;
            results?: Array<{
              lat: number;
              lon: number;
              name: string;
              country: string;
              state?: string;
              population?: number;
              placeId?: string;
            }>;
          }
        >(functions, 'geocodeLocation', 6000);
        const geocodeResult = await geocodeLocation({
          query,
          ...(countryHint ? { country: countryHint } : {}),
        });

        if (geocodeResult && geocodeResult.data) {
          const data = geocodeResult.data as any;
          // Cloud Function may return results: [] (no throw) or full payload
          const rawCandidates: any[] = Array.isArray(data?.results) ? data.results : [];
          if (typeof data?.lat === 'number' && typeof data?.lon === 'number') {
            rawCandidates.unshift({
              name: data?.name || query,
              country: data?.country,
              state: data?.state,
              lat: data?.lat,
              lon: data?.lon,
              population: data?.population,
            });
          }

          const normalizedQuery = queryParts[0]?.toLowerCase() || query.toLowerCase();
          const rankedCandidates = rawCandidates
            .map((item, index) => {
              const cityName = `${item?.name || item?.city || ''}`.toLowerCase().trim();
              const matchTier = cityName === normalizedQuery ? 2 : cityName.startsWith(normalizedQuery) ? 1 : 0;
              return { item, index, cityName, matchTier };
            })
            .sort((a, b) => {
              if (b.matchTier !== a.matchTier) return b.matchTier - a.matchTier;
              if (a.cityName && a.cityName === b.cityName) {
                return (b.item?.population ?? 0) - (a.item?.population ?? 0);
              }
              return a.index - b.index;
            });

          rankedCandidates.forEach(({ item }) => {
            const city = item?.name || item?.city || '';
            const country = item?.country || '';
            const latitude = item?.lat ?? item?.latitude;
            const longitude = item?.lon ?? item?.longitude;
            const state = item?.state;

            if (city && country && typeof latitude === 'number' && typeof longitude === 'number') {
              const fullName = `${city}${state ? `, ${state}` : ''}, ${country}`;
              geocodeSuggestions.push({
                city,
                state,
                country,
                placeId: item?.placeId,
                fullName,
                coordinates: { latitude, longitude },
              });
            }
          });
        }
      } catch (cloudFunctionError: any) {
        // Treat not-found (and any CF error) as empty results so local suggestions + hint still show
        const code = cloudFunctionError?.code ?? '';
        if (code === 'functions/not-found' || `${cloudFunctionError?.message || ''}`.toLowerCase().includes('not found')) {
          // Leave geocodeSuggestions empty; combined will still have localResults
        } else {
          console.error('[CityAutocomplete] Cloud Function error:', cloudFunctionError);
        }
      }
      
      // Prefer geocoded matches (with coordinates) before local seeds, then de-dupe by full name.
      const combined = [...geocodeSuggestions, ...localResults];
      const seen = new Set<string>();
      const results: CitySuggestion[] = [];

      combined.forEach((item) => {
        const key = item.fullName.toLowerCase();
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        // Attach ISO2 when we can (helps downstream: visa, advisories).
        const countryCode = item.country ? (toIso2CountryCode(item.country) || undefined) : undefined;
        results.push({
          ...item,
          countryCode,
        });
      });

      const finalResults = results.slice(0, MAX_RESULTS);
      cacheRef.current[key] = finalResults;
      lastResultsRef.current = finalResults;

      setSuggestionState(finalResults, finalResults.length === 0);
    } catch (error) {
      console.error('[CityAutocomplete] Unexpected error searching cities:', error);
      if (lastResultsRef.current.length > 0) {
        setSuggestionState(lastResultsRef.current, false);
      } else {
        setSuggestionState([], true);
      }
    } finally {
      setLoading(false);
      loadingKeyRef.current = null;
    }
  };

  // NOTE: We intentionally avoid measuring + full-screen Modal for suggestions.
  // Inline dropdown prevents the overlay from blocking keyboard input.
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keyboard listeners no longer needed for inline suggestions

  const handleUseAsEntered = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    isSelectingRef.current = true;
    dismissedRef.current = true;
    lastSelectedValueRef.current = trimmed;
    setShowSuggestions(false);
    setShowNoResultsHint(false);
    setSuggestions([]);
    onChangeText(trimmed);
    if (onSelectCity) {
      onSelectCity({
        city: trimmed.includes(',') ? trimmed.split(',')[0]?.trim() || trimmed : trimmed,
        country: trimmed.includes(',') ? trimmed.split(',').slice(1).join(',').trim() : '',
        fullName: trimmed,
      });
    }
    setTimeout(() => {
      isSelectingRef.current = false;
      inputRef.current?.focus();
    }, 50);
  };

  const handleSelectCity = (suggestion: CitySuggestion) => {
    isSelectingRef.current = true;
    isShowingRef.current = false;
    dismissedRef.current = false;
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    dismissedRef.current = true;
    lastSelectedValueRef.current = suggestion.fullName;
    setShowSuggestions(false);
    setShowNoResultsHint(false);
    setSuggestions([]);
    
    // Update the input value
    onChangeText(suggestion.fullName);
    
    if (onSelectCity) {
      onSelectCity(suggestion);
    }
    
    // Reset selecting flag after a delay (keep focus so user can continue typing)
    setTimeout(() => {
      isSelectingRef.current = false;
      inputRef.current?.focus();
    }, 50);
  };

  const handleClose = useCallback(() => {
    isShowingRef.current = false;
    dismissedRef.current = true; // Don't re-open while user keeps typing in this focus session
    setShowSuggestions(false);
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  }, []);

  // Note: we intentionally do NOT auto-close suggestions on a timer.
  // Auto-close + debounced search caused the dropdown to "keep popping up" while typing.

  // Close suggestions when component loses focus (e.g., navigation)
  useEffect(() => {
    return () => {
      // Cleanup: close suggestions on unmount
      setShowSuggestions(false);
    };
  }, []);

  // Ensure suggestions close when component unmounts or value changes externally
  useEffect(() => {
    if (!value || value.trim().length === 0) {
      setShowSuggestions(false);
      setShowNoResultsHint(false);
      isShowingRef.current = false;
      dismissedRef.current = false;
    }
    if (value.trim().length < minQueryLength) {
      setShowNoResultsHint(false);
    }
  }, [value, minQueryLength]);

  return (
    <>
      <View collapsable={false} style={{ position: 'relative', zIndex: 50 }}>
        <InputField
          ref={inputRef}
          label={label}
          placeholder={placeholder}
          value={value}
          onChangeText={(text) => {
            // User is typing again; allow suggestions to show for new input.
            if (lastSelectedValueRef.current) {
              lastSelectedValueRef.current = null;
            }
            dismissedRef.current = false;
            const trimmed = text.trim();
            if (trimmed.length >= minQueryLength) {
              const local = buildLocalSuggestions(trimmed);
              if (local.length > 0) {
                lastResultsRef.current = local;
                setSuggestionState(local, false);
              }
            } else {
              setShowNoResultsHint(false);
            }
            onChangeText(text);
          }}
          errorText={errorText}
          autoCapitalize={autoCapitalize}
          autoCorrect={false}
          autoComplete="off"
          textContentType="none"
          onFocus={() => {
            isFocusedRef.current = true;
            dismissedRef.current = false;
            // Don't show if currently selecting
            if (isSelectingRef.current) {
              return;
            }
            // If empty, show popular destinations so the user can pick without typing.
            if ((!value || value.trim().length === 0) && !dismissedRef.current) {
              const popular = buildPopularSuggestions();
              setSuggestions(popular);
              lastResultsRef.current = popular;
              isShowingRef.current = popular.length > 0;
              setShowSuggestions(popular.length > 0);
              return;
            }

            // Show suggestions if we have any
            if (suggestions.length > 0 && !dismissedRef.current) {
              isShowingRef.current = true;
              setShowSuggestions(true);
            }
          }}
          returnKeyType="search"
          blurOnSubmit={false}
          onSubmitEditing={() => {
            // If the user typed but didn't tap a dropdown item, auto-select the top suggestion.
            if (suggestions.length > 0 && value.trim().length > 0) {
              handleSelectCity(suggestions[0]);
              return;
            }
            // If empty, open popular suggestions.
            if (!value || value.trim().length === 0) {
              const popular = buildPopularSuggestions();
              setSuggestions(popular);
              lastResultsRef.current = popular;
              isShowingRef.current = popular.length > 0;
              setShowSuggestions(popular.length > 0);
            }
          }}
          onBlur={() => {
            isFocusedRef.current = false;
            dismissedRef.current = false;
            // Clear any existing timeout
            if (blurTimeoutRef.current) {
              clearTimeout(blurTimeoutRef.current);
            }
            // Delay hiding suggestions to allow selection - increased delay to prevent flickering
            blurTimeoutRef.current = setTimeout(() => {
              if (!isShowingRef.current) {
                setShowSuggestions(false);
                isShowingRef.current = false;
              }
              blurTimeoutRef.current = null;
            }, 300);
          }}
        />

        {/* Clear button for quick edits */}
        {value?.length > 0 && (
          <Pressable
            onPress={() => {
              lastSelectedValueRef.current = null;
              dismissedRef.current = false;
              onChangeText('');
              setSuggestions([]);
              setShowSuggestions(false);
              isShowingRef.current = false;
            }}
            hitSlop={10}
            style={{
              position: 'absolute',
              right: theme.spacing(2.5),
              top: label ? 38 : theme.spacing(1.5),
            }}
          >
            <Ionicons name="close-circle" size={18} color={theme.colors.textSecondary} />
          </Pressable>
        )}

        {/* Loading indicator */}
        {loading && (
          <View style={{ position: 'absolute', right: theme.spacing(2), top: label ? 40 : theme.spacing(1.5), zIndex: 1 }}>
            <ActivityIndicator size="small" color={theme.colors.primary} />
          </View>
        )}

        {/* Inline suggestions dropdown or no-results hint (no Modal overlay) */}
        {showSuggestions && (suggestions.length > 0 || showNoResultsHint) && (
          <View style={{ marginTop: theme.spacing(0.5), zIndex: 9999, elevation: 30 }}>
            <Card
              variant="outlined"
              style={{
                padding: 0,
                backgroundColor: theme.colors.surface,
                elevation: 30,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.25,
                shadowRadius: 6,
              }}
            >
              {suggestions.length > 0 ? (
                <ScrollView keyboardShouldPersistTaps="always" nestedScrollEnabled style={{ maxHeight: 200 }}>
                  <View style={{ gap: theme.spacing(0.5), padding: theme.spacing(1) }}>
                    {suggestions.map((suggestion, index) => (
                      <Pressable
                        key={`${suggestion.fullName}-${index}`}
                        onPress={() => handleSelectCity(suggestion)}
                        style={({ pressed }) => ({
                          padding: theme.spacing(1.5),
                          borderRadius: theme.radii.md,
                          backgroundColor: pressed ? theme.colors.borderMuted + '40' : 'transparent',
                        })}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                          <Ionicons name="location" size={18} color={theme.colors.primary} />
                          <View style={{ flex: 1 }}>
                            <ThemedText variant="bodyStrong" style={{ fontSize: 15 }}>
                              {suggestion.fullName}
                            </ThemedText>
                            {suggestion.state && (
                              <Caption color={theme.colors.textSecondary} style={{ fontSize: 12, marginTop: 2 }}>
                                {suggestion.country}
                              </Caption>
                            )}
                          </View>
                        </View>
                      </Pressable>
                    ))}
                  </View>
                </ScrollView>
              ) : (
                <View style={{ padding: theme.spacing(1.5), gap: theme.spacing(1) }}>
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
                    {NO_RESULTS_HINT}
                  </Caption>
                  {allowUseAsEntered && value.trim().length > 0 && (
                    <Pressable
                      onPress={handleUseAsEntered}
                      style={({ pressed }) => ({
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                        padding: theme.spacing(1.5),
                        borderRadius: theme.radii.md,
                        backgroundColor: pressed ? theme.colors.borderMuted + '40' : 'transparent',
                      })}
                    >
                      <Ionicons name="checkmark-circle-outline" size={18} color={theme.colors.primary} />
                      <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
                        Use &quot;{value.trim()}&quot; as entered
                      </ThemedText>
                    </Pressable>
                  )}
                </View>
              )}
            </Card>
          </View>
        )}
      </View>
    </>
  );
}

